/**
 * O idioma da loja quando a pessoa ainda não escolheu.
 *
 * Antes era o primeiro idioma suportado da lista do navegador: quem está no
 * Brasil com o navegador em inglês caía em inglês, e a detecção ficava gravada
 * (2026-11-02). Agora quem parece estar no Brasil (fuso horário brasileiro ou
 * português em qualquer posição da lista) abre em português; os outros seguem
 * a lista do navegador. Só a escolha feita no seletor de idioma fica lembrada.
 * A regra é a mesma da moeda do site de vendas (`apps/web/lib/v2/moeda.ts`).
 */

export const IDIOMAS = ['pt', 'en', 'es'] as const
export type Idioma = (typeof IDIOMAS)[number]

/** Onde fica a escolha feita no seletor. A chave antiga (`i18nextLng`) guardava a detecção e é ignorada. */
export const CHAVE_DO_IDIOMA = 'hystore:idioma'

/** Fusos do Brasil (IANA), inclusive os nomes antigos. */
const FUSOS_DO_BRASIL = new Set([
  'America/Sao_Paulo', 'America/Rio_Branco', 'America/Manaus', 'America/Cuiaba', 'America/Campo_Grande', 'America/Belem',
  'America/Fortaleza', 'America/Recife', 'America/Bahia', 'America/Maceio', 'America/Araguaina', 'America/Santarem',
  'America/Porto_Velho', 'America/Boa_Vista', 'America/Eirunepe', 'America/Noronha',
  'Brazil/East', 'Brazil/West', 'Brazil/Acre', 'Brazil/DeNoronha',
])

export interface SinaisDoNavegador {
  idiomas: readonly string[]
  fuso: string | null
}

const ehIdioma = (v: unknown): v is Idioma => typeof v === 'string' && (IDIOMAS as readonly string[]).indexOf(v) >= 0

export function sinaisDoNavegador(): SinaisDoNavegador {
  if (typeof navigator === 'undefined') return { idiomas: [], fuso: null }
  let fuso: string | null = null
  try {
    fuso = Intl.DateTimeFormat().resolvedOptions().timeZone || null
  } catch {
    fuso = null
  }
  const idiomas = navigator.languages && navigator.languages.length ? navigator.languages : navigator.language ? [navigator.language] : []
  return { idiomas, fuso }
}

export function pareceDoBrasil({ idiomas, fuso }: SinaisDoNavegador): boolean {
  if (fuso && FUSOS_DO_BRASIL.has(fuso)) return true
  return idiomas.some((i) => /^pt(-|$)/i.test(i.trim()))
}

/** Sem escolha guardada: português para quem parece estar no Brasil; senão, o primeiro da lista que a loja fala. */
export function idiomaInicial(sinais: SinaisDoNavegador): Idioma {
  if (pareceDoBrasil(sinais)) return 'pt'
  for (const i of sinais.idiomas) {
    const base = i.trim().split('-')[0].toLowerCase()
    if (ehIdioma(base)) return base
  }
  return 'pt'
}

export function idiomaEscolhido(): Idioma | null {
  try {
    const v = localStorage.getItem(CHAVE_DO_IDIOMA)
    return ehIdioma(v) ? v : null
  } catch {
    return null
  }
}

export function guardarEscolha(idioma: string): void {
  if (!ehIdioma(idioma)) return
  try {
    localStorage.setItem(CHAVE_DO_IDIOMA, idioma)
  } catch {
    // navegador sem armazenamento: só não lembra na próxima visita
  }
}
